import React from "react";
import { Navigate, Route } from "react-router-dom";
import { UserRoleEnum } from "@/types/role.type";
import type { RouteCtx } from "../routeContext";
import { NotFound } from "../shell";
import {
  DataReviewPage,
  StudyDashboard,
  CodeListsPage,
  ExportsPage,
  RegulatoryApprovalsMain,
  ApprovalDetailPage,
  ActivityHistory,
  CTMSDataReviewDashboard,
  CRFDesigner,
  CRFTemplates,
  SubjectsManagement,
  VisitDataEntry,
  EDCDataReviewPage,
  DataCompletenessDashboard,
  ExportsEDC,
  DocumentLibrary,
  UploadDocument,
  QCReview,
  ETMFCompletenessDashboard,
  RetentionArchival,
  InspectionMode,
  ModelGovernance,
  MigrationReview,
  CDMSDataReviewPage,
  QueryManagementPage,
  SDVSDRPage,
  CodingPage,
  DataLocksPage,
  DataExtractsPage,
  CDMSDashboardsPage,
  CTMSReportsLanding,
  IssueRiskTrackerPage,
  RBQMCenter,
} from "../lazyPages";

/** dataManagerRoutes — order preserved for React Router first-match. */
export function dataManagerRoutes(ctx: RouteCtx): React.ReactNode {
  const {
    userRole,
    selectedStudyId,
    availableStudies,
    hasRbqmAccess,
    currentStudy,
  } = ctx;

  if (userRole !== UserRoleEnum.DATA_MANAGER) {
    return null;
  }

  return (
    <>
      {/* Dashboard Routes */}
      <Route
        path="/study-dashboard"
        element={<StudyDashboard currentStudy={currentStudy} />}
      />
      <Route path="/activity-history" element={<ActivityHistory />} />

      {/* CTMS Routes */}
      <Route
        path="/ctms/data-review-dashboard"
        element={<CTMSDataReviewDashboard currentStudy={currentStudy} />}
      />
      <Route
        path="/ctms/data-audit-trail"
        element={<Navigate to="/admin/audit-logs" replace />}
      />

      {/* EDC Routes */}
      <Route path="/edc/crf-designer" element={<CRFDesigner />} />
      <Route
        path="/edc/crf-templates"
        element={<CRFTemplates userRole={userRole} />}
      />
      <Route
        path="/edc/subjects"
        element={
          <SubjectsManagement userRole={userRole} currentStudy={currentStudy} />
        }
      />
      <Route
        path="/edc/visit-data-entry"
        element={<VisitDataEntry userRole={userRole} />}
      />
      <Route
        path="/edc/data-review"
        element={<EDCDataReviewPage userRole={userRole} />}
      />
      <Route
        path="/edc/data-completeness"
        element={
          <DataCompletenessDashboard
            userRole={userRole}
            currentStudy={currentStudy}
          />
        }
      />
      <Route path="/edc/exports" element={<ExportsEDC />} />

      {/* eTMF Routes */}
      <Route
        path="/etmf/document-library"
        element={
          <DocumentLibrary
            userRole={userRole}
            selectedStudyId={selectedStudyId}
          />
        }
      />
      <Route
        path="/etmf/upload-document"
        element={
          <UploadDocument
            userRole={userRole}
            selectedStudyId={selectedStudyId}
            currentStudy={
              availableStudies?.find((s) => s.id === selectedStudyId) ?? null
            }
          />
        }
      />
      <Route
        path="/etmf/qc-review"
        element={
          <QCReview userRole={userRole} selectedStudyId={selectedStudyId} />
        }
      />
      <Route
        path="/etmf/completeness-dashboard"
        element={
          <ETMFCompletenessDashboard selectedStudyId={selectedStudyId} />
        }
      />
      <Route path="/etmf/retention-archival" element={<RetentionArchival />} />
      <Route
        path="/etmf/inspection-mode"
        element={
          <InspectionMode
            userRole={userRole}
            selectedStudyId={selectedStudyId}
            currentStudy={
              availableStudies?.find((s) => s.id === selectedStudyId) ?? null
            }
          />
        }
      />
      <Route
        path="/etmf/model-governance"
        element={
          <ModelGovernance
            userRole={userRole}
            selectedStudyId={selectedStudyId}
            currentStudy={
              availableStudies?.find((s) => s.id === selectedStudyId) ?? null
            }
          />
        }
      />
      <Route
        path="/etmf/migration-review"
        element={
          <MigrationReview
            userRole={userRole}
            selectedStudyId={selectedStudyId}
            currentStudy={
              availableStudies?.find((s) => s.id === selectedStudyId) ?? null
            }
          />
        }
      />

      {/* CDMS Routes */}
      <Route path="/cdms/data-review" element={<CDMSDataReviewPage />} />
      <Route
        path="/cdms/query-management"
        element={
          <QueryManagementPage
            userRole={userRole}
            currentStudy={currentStudy}
          />
        }
      />
      <Route path="/cdms/sdv-sdr" element={<SDVSDRPage />} />
      <Route path="/cdms/coding" element={<CodingPage />} />
      <Route path="/cdms/data-locks" element={<DataLocksPage />} />
      <Route path="/cdms/data-extracts" element={<DataExtractsPage />} />
      <Route
        path="/cdms/dashboards"
        element={<CDMSDashboardsPage currentStudy={currentStudy} />}
      />

      {/* Data Management Routes */}
      <Route path="/data-review" element={<DataReviewPage />} />
      <Route path="/exports" element={<ExportsPage />} />

      {/* Reports Routes */}
      <Route
        path="/ctms-reports"
        element={<CTMSReportsLanding userRole={userRole} />}
      />

      {/* Legacy/Other Routes */}
      <Route path="/code-lists" element={<CodeListsPage />} />
      <Route
        path="/dm/regulatory"
        element={
          <RegulatoryApprovalsMain
            userRole={userRole}
            currentStudy={currentStudy}
          />
        }
      />
      <Route
        path="/dm/regulatory/:studyId/:id"
        element={<ApprovalDetailPage />}
      />
      <Route
        path="/dm/risk-tracker"
        element={
          <IssueRiskTrackerPage
            userRole={userRole}
            currentStudy={currentStudy}
          />
        }
      />
      <Route
        path="/dm/rbqm-center"
        element={
          hasRbqmAccess ? (
            <RBQMCenter userRole={userRole} currentStudy={currentStudy} />
          ) : (
            <NotFound />
          )
        }
      />
    </>
  );
}
